import moment from "moment";

/**
 * Build Chart.js line data for stall_comparison_timeline.
 * Snaps sub-hour points into the same hour and picks labels so
 * Takings and Product Sales look the same:
 * - hourly (same or multi-day) → DD/MM HH:mm (17/08 16:00, …)
 * - multi-day daily buckets (same hour each day) → DD/MM
 *
 * The API is expected to send the full filter window (including zero
 * buckets), so the x-axis matches the Event Filter start/end.
 *
 * @param {Array} timelineData - API stall_comparison_timeline
 * @param {Function} getColor - (index) => color string
 * @returns {{ labels: string[], datasets: object[] }}
 */
export function buildStallComparisonTimelineChartData(timelineData, getColor) {
  if (!timelineData?.length || !timelineData[0]?.Stalls?.length) {
    return emptyTimelineChartData();
  }

  // Drop stalls that have all-zero data (e.g. when a stall filter is active)
  const activeTimelineData = timelineData.filter((obj) =>
    obj.Stalls?.some((s) => s.SalesAmount > 0)
  );
  const dataToRender =
    activeTimelineData.length > 0 ? activeTimelineData : timelineData;

  // Snap SortKey / Timestamp to the start of the hour (YYYYMMDDHH00)
  const hourKeyFor = (s) => {
    const raw = s.SortKey ?? s.Timestamp;
    const parsed = moment(
      raw,
      [
        "YYYYMMDDHHmm",
        "DD/MM HH:mm",
        "DD/MM HH:00",
        "DD/MM/YYYY HH:mm",
        "DD/MM",
        "MM/DD",
        "HH:mm",
      ],
      true
    );
    if (parsed.isValid()) {
      return parsed.startOf("hour").format("YYYYMMDDHHmm");
    }
    // Fallback: if raw looks like YYYYMMDDHHmm, zero the minutes
    if (typeof raw === "string" && /^\d{12}$/.test(raw)) {
      return raw.slice(0, 10) + "00";
    }
    // Daily key YYYYMMDD → treat as midnight that day
    if (typeof raw === "string" && /^\d{8}$/.test(raw)) {
      return raw + "0000";
    }
    return String(raw);
  };

  const hourKeysSet = {};
  dataToRender.forEach((stall) =>
    (stall.Stalls || []).forEach((s) => {
      hourKeysSet[hourKeyFor(s)] = true;
    })
  );

  const allKeys = Object.keys(hourKeysSet).sort();
  const labelFormat = pickLabelFormat(allKeys);
  const labels = allKeys.map((k) =>
    moment(k, "YYYYMMDDHHmm").format(labelFormat)
  );

  return {
    labels,
    datasets: dataToRender.map((obj, index) => {
      // Sum amounts that fall in the same hour (takings may send sub-hour buckets)
      const dataByKey = {};
      (obj.Stalls || []).forEach((s) => {
        const key = hourKeyFor(s);
        const amount = parseFloat(s.SalesAmount);
        dataByKey[key] =
          (dataByKey[key] || 0) + (Number.isFinite(amount) ? amount : 0);
      });

      const color = getColor(index);
      return {
        label: obj["Stall Name"],
        borderColor: color,
        pointBackgroundColor: "white",
        borderWidth: 3,
        pointBorderColor: color,
        backgroundColor: "transparent",
        data: allKeys.map((k) =>
          Object.prototype.hasOwnProperty.call(dataByKey, k)
            ? dataByKey[k]
            : null
        ),
      };
    }),
  };
}

/**
 * Choose x-axis label format from the set of hourly keys.
 * Always include day/month for hourly ticks; date-only when
 * multi-day daily buckets would otherwise all show the same time.
 */
function pickLabelFormat(allKeys) {
  if (!allKeys.length) return "DD/MM HH:mm";

  const days = new Set(allKeys.map((k) => k.slice(0, 8)));
  const hours = new Set(allKeys.map((k) => k.slice(8, 12)));

  // Daily (or same-clock-time) buckets across days → date only
  if (days.size > 1 && hours.size === 1) {
    return "DD/MM";
  }

  // Same-day hourly and multi-day hourly → include day/month + time
  return "DD/MM HH:mm";
}

function emptyTimelineChartData() {
  return {
    labels: ["No Data Available"],
    datasets: [
      {
        label: "No Data Available",
        borderColor: "#FF0E00",
        pointBackgroundColor: "white",
        borderWidth: 1,
        pointBorderColor: "#FF0E00",
        backgroundColor: "transparent",
        data: [0],
      },
    ],
  };
}
